import { useCallback } from 'react';

import { OasMemoryError, OasError } from 'core/atoms/errors';

import { gql, useMutation } from 'core/dna/types/apollo';

import { UpdateEvaluationBatchInput } from 'core/dna/types/generated/_globalTypes';
import {
  UpdateEvaluationBatch,
  UpdateEvaluationBatchVariables,
} from 'core/dna/types/generated/UpdateEvaluationBatch';

import { EVALUATION_BATCH_FRAGMENT } from 'modules/grades/memory/apollo/_fragments';

import { GET_EVALUATION_BATCH_LIST_QUERY } from './use-get-evaluation-batch-list';

export const UPDATE_EVALUATION_BATCH_MUTATION = gql`
  mutation UpdateEvaluationBatch($input: UpdateEvaluationBatchInput) {
    updateEvaluationBatch(input: $input) {
      ...EvaluationBatch
    }
  }
  ${EVALUATION_BATCH_FRAGMENT}
`;

const TITLE = 'use-update-evaluation-batch';

export const useUpdateEvaluationBatch = () => {
  const [updateEvaluationBatch, { loading, error }] = useMutation<
    UpdateEvaluationBatch,
    UpdateEvaluationBatchVariables
  >(UPDATE_EVALUATION_BATCH_MUTATION);

  const func = useCallback(
    async (input: UpdateEvaluationBatchInput) => {
      try {
        const result = await updateEvaluationBatch({
          variables: {
            input: { ...input },
          },
          refetchQueries: [
            {
              query: GET_EVALUATION_BATCH_LIST_QUERY,
            },
          ],
        });

        if (result.errors) {
          throw new OasMemoryError(JSON.stringify(result.errors), {
            title: TITLE,
          });
        }
        return result.data?.updateEvaluationBatch ?? undefined;
      } catch (e: any) {
        if (e instanceof OasError) {
          throw e;
        } else {
          throw OasMemoryError.fromError(e, {
            title: TITLE,
          });
        }
      }
    },
    [updateEvaluationBatch],
  );

  return {
    updateEvaluationBatch: func,
    loading,
    error,
  };
};
